import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { motion, MotionConfig } from "framer-motion";
import AdventureArt from "../../components/AdventureArt";
import "./kids.css";
import { publicApi, apiError, KID_KEY, storage } from "../../lib/api";
import { Button } from "../../components/ui";
import Avatar from "../../components/Avatar";
import { BrandLogo } from "../../components/Mascot";
import Icon from "../../components/Icon";
import { displayColor } from "../../lib/theme";

const KIOSK_KEY = "choremouse_kiosk";

export default function KidsEntry() {
  const navigate = useNavigate();
  const [step, setStep] = useState("code");
  const [code, setCode] = useState("");
  const [family, setFamily] = useState(null);
  const [kids, setKids] = useState([]);
  const [selected, setSelected] = useState(null);
  const [pin, setPin] = useState("");
  const [loading, setLoading] = useState(false);

  const runVerify = async (theCode, fromKiosk = false) => {
    setLoading(true);
    try {
      const { data } = await publicApi.post("/kids/verify-code", { code: theCode });
      setFamily(data.family_name); setKids(data.children); setStep("kid");
      storage.set(KIOSK_KEY, theCode.toUpperCase());
    } catch (err) {
      if (fromKiosk) { storage.remove(KIOSK_KEY); setStep("code"); }
      else { toast.error(apiError(err)); }
    } finally { setLoading(false); }
  };

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const urlKiosk = params.get("kiosk");
    let kc = storage.get(KIOSK_KEY);
    if (urlKiosk) { kc = urlKiosk.toUpperCase().replace(/\s/g, ""); storage.set(KIOSK_KEY, kc); window.history.replaceState({}, "", "/kids"); }
    if (kc) { setCode(kc); runVerify(kc, true); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const verify = (e) => { e?.preventDefault(); runVerify(code); };

  const exitKiosk = () => { storage.remove(KIOSK_KEY); setCode(""); setKids([]); setStep("code"); };

  const pickKid = (k) => {
    setSelected(k);
    if (k.has_pin) { setPin(""); setStep("pin"); }
    else doLogin(k, null);
  };

  const doLogin = async (kid, thePin) => {
    setLoading(true);
    try {
      const { data } = await publicApi.post("/kids/login", { code, child_id: kid.id, pin: thePin });
      storage.set(KID_KEY, data.token);
      navigate("/kids/play");
    } catch (err) { toast.error(apiError(err)); }
    finally { setLoading(false); }
  };

  return (
    <MotionConfig reducedMotion="user"><div className="kid-world kid-entry min-h-screen">
      <div className="kid-entry-layout">
        <section className="kid-entry-art" aria-label="Welcome to Mission HQ">
          <span className="quest-eyebrow">SMALL MISSIONS. BIG ADVENTURES.</span>
          <h1>Your world.<br />Your next adventure.</h1>
          <p>Help your crew. Collect coins. Make good things happen.</p>
          <AdventureArt className="entry-island" />
          <div className="adventure-steps"><span>01 · Pick a mission</span><span>02 · Earn coins</span><span>03 · Choose rewards</span></div>
        </section>
        <div className="kid-entry-panel">
        <button onClick={() => navigate("/")} className="flex items-center gap-2 mb-6 self-start" data-testid="kids-brand">
          <BrandLogo size={44} />
          <span className="font-heading font-black text-2xl text-ink">ChoreMouse</span>
        </button>

        {step === "code" && (
          <motion.form initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} onSubmit={verify} data-testid="kids-code-form">
            <span className="quest-section-label">WELCOME TO MISSION HQ</span>
            <h2 className="font-heading font-black text-3xl text-ink mt-2 mb-2">Ready, player?</h2>
            <p className="text-ink-soft font-semibold text-base mb-6">Enter your family code to join your crew. A parent can find it in their family settings.</p>
            <input autoFocus value={code} onChange={(e) => setCode(e.target.value.toUpperCase().replace(/\s/g, ""))}
              aria-label="Family code" autoComplete="off" placeholder="CHEESE" maxLength={8} data-testid="kids-code-input"
              className="w-full text-center font-heading font-black text-3xl tracking-[0.2em] uppercase rounded-4xl border-4 border-primary/30 bg-white py-6 text-primary-dark focus-ring placeholder:text-primary/20" />
            <Button type="submit" size="lg" className="w-full mt-6" disabled={loading || !code} data-testid="kids-code-submit">
              {loading ? "Checking…" : <>Enter Mission HQ <Icon name="arrow-right" width={22} height={22} /></>}
            </Button>
            <button type="button" onClick={() => navigate("/login")} className="mt-6 w-full text-center font-bold text-ink-soft" data-testid="kids-parent-link">I'm a grown-up →</button>
          </motion.form>
        )}

        {step === "kid" && (
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} data-testid="kids-picker">
            <span className="quest-section-label">{family}</span>
            <h2 className="font-heading font-black text-3xl text-ink mt-2 mb-1">Choose your player</h2>
            <p className="text-ink-soft font-semibold text-lg mb-6">Your missions are waiting.</p>
            {kids.length === 0 ? (
              <div className="bg-white rounded-4xl p-8 text-center font-bold text-ink">No kids added yet. Ask a grown-up to add you!</div>
            ) : (
              <div className="grid grid-cols-2 gap-4">
                {kids.map((k) => (
                  <button key={k.id} onClick={() => pickKid(k)} data-testid={`pick-kid-${k.id}`}
                    disabled={loading} className="player-card" style={{ "--player-color": displayColor(k.color) }}>
                    <Avatar avatar={k.avatar} color={k.color} size={84} />
                    <span className="font-heading font-extrabold text-lg text-ink">{k.name}</span>
                    {k.has_pin && <span className="text-xs font-bold text-ink-muted flex items-center gap-1"><Icon name="lock" width={12} height={12} /> PIN</span>}
                  </button>
                ))}
              </div>
            )}
            <button onClick={exitKiosk} className="mt-8 w-full text-center text-xs font-bold text-ink-muted/70" data-testid="kiosk-exit">⚙ Grown-up: switch family / leave kiosk</button>
          </motion.div>
        )}

        {step === "pin" && (
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} data-testid="kids-pin">
            <div className="flex flex-col items-center mb-6"><Avatar avatar={selected.avatar} color={selected.color} size={84} /><h1 className="font-heading font-black text-3xl text-ink mt-3">Hi {selected.name}!</h1><p className="text-ink-soft font-semibold">Enter your PIN</p></div>
            <div className="flex justify-center gap-3 mb-6">
              {[0, 1, 2, 3].map((i) => (<div key={i} className={`w-14 h-16 rounded-2xl border-4 grid place-items-center font-heading font-black text-2xl ${pin.length > i ? "border-primary bg-primary/5 text-primary-dark" : "border-slate-200 bg-white"}`}>{pin[i] ? "●" : ""}</div>))}
            </div>
            <div className="grid grid-cols-3 gap-3 max-w-xs mx-auto">
              {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((n) => (
                <button key={n} onClick={() => setPin((p) => (p.length < 4 ? p + n : p))} data-testid={`pin-${n}`} className="h-16 rounded-2xl bg-white border-2 border-slate-200 font-heading font-black text-2xl text-ink active:scale-95 transition-transform">{n}</button>
              ))}
              <button onClick={() => setPin("")} className="h-16 rounded-2xl bg-slate-100 font-bold text-ink-soft" data-testid="pin-clear">Clear</button>
              <button onClick={() => setPin((p) => (p.length < 4 ? p + "0" : p))} data-testid="pin-0" className="h-16 rounded-2xl bg-white border-2 border-slate-200 font-heading font-black text-2xl text-ink active:scale-95 transition-transform">0</button>
              <button onClick={() => setPin((p) => p.slice(0, -1))} className="h-16 rounded-2xl bg-slate-100 grid place-items-center text-ink-soft" data-testid="pin-del" aria-label="Delete last PIN digit"><Icon name="delete" width={24} height={24} /></button>
            </div>
            <Button size="lg" className="w-full mt-6" disabled={pin.length < 4 || loading} onClick={() => doLogin(selected, pin)} data-testid="pin-submit">{loading ? "…" : "Start my adventure"}</Button>
            <button onClick={() => setStep("kid")} className="mt-4 w-full text-center font-bold text-ink-soft" data-testid="kids-back-picker">← Back</button>
          </motion.div>
        )}
        </div>
      </div>
    </div></MotionConfig>
  );
}
